import { api } from '../../../api/api';
import { LoginResponse, RequestLogin } from './types';

const AUTH_TAG = 'Auth';

export const authApi = api.enhanceEndpoints({ addTagTypes: [AUTH_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    requestLogin: build.mutation<RequestLogin, { email: string; redirect?: string }>({
      query: ({ email, redirect }) => ({
        method: 'POST',
        url: 'auth/request-login',
        body: { email, ...(redirect ? { redirect } : {}) }
      })
    }),
    login: build.query<LoginResponse, { token: string }>({
      query: ({ token }) => ({
        method: 'GET',
        url: 'auth/login',
        params: { token }
      })
    }),
    getUserData: build.query<{ data: { email: string } }, { token: string }>({
      query: ({ token }) => ({
        method: 'GET',
        url: 'auth/user-data',
        params: { token }
      })
    }),
    // Generate share link for customer (team only)
    generateShareLink: build.mutation<{ success: boolean; message: string; magicLink?: string }, { email: string,  redirect?: string }>({
      query: (credentials) => ({
        method: 'POST',
        url: 'auth/generate-share-link',
        body: credentials
      })
    }),
    // Team-only: search customers by email, ICCID or Stripe customer ID
    // (staff session required). Results are keyed by email.
    teamSearchUsers: build.mutation<
      {
        success: boolean;
        emails: string[];
        results: Array<{
          email: string;
          matchedVia: 'email' | 'iccid' | 'stripe';
          matchedValue: string;
        }>;
      },
      { query: string; }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'auth/team-search-users',
        body
      })
    }),
    // Team-only: switch the active admin session to another customer
    // while retaining the named staff session.
    teamSwitchUser: build.mutation<
      { success: boolean; message: string; token: string; email: string },
      { email: string; }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'auth/team-switch-user',
        body
      })
    }),
    // Team-only: subscriber-base health reports. One call computes ALL
    // reports (orphans / active fleet / unpaid / barred-without-pause) from a
    // single Pelion+DB+Stripe sweep, so the UI can switch views instantly.
    simHealthReport: build.mutation<
      {
        success: boolean;
        generated_at: string;
        reports: Record<
          'orphans' | 'active' | 'unpaid' | 'barred-no-pause',
          {
            tiles: Array<{ label: string; value: number; highlight?: boolean }>;
            rows: Array<{
              iccid: string;
              email: string;
              stripe_id: string;
              plan: string;
              operator_name: string;
              tariff_name: string;
              connection_date: string;
              is_barred: boolean;
              note: string;
              alt_stripe_id: string;
            }>;
          }
        > & {
          usage: {
            tiles: Array<{ label: string; value: number; highlight?: boolean }>;
            rows: Array<{
              iccid: string;
              email: string;
              device: string;
              operator_name: string;
              tariff_name: string;
              sim_state: string;
              plan_gb: number;
              used_gb: number;
              projected_gb: number;
              pct_used: number | null;
              over_gb: number;
              over_cost_gbp: number;
              note: string;
            }>;
          };
        };
      },
      { }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'admin/sim-health',
        body
      })
    }),
    // Team-only, read-only: dunning pipeline state for the /callum dashboard.
    overuseDunningStatus: build.mutation<
      {
        success: boolean;
        month: string;
        today_day: number;
        config: {
          max_recipients: number;
          min_cost_gbp: number;
          hard_limit_gb: number;
          grace_gb: number;
          projection_factor: number;
          auto_bar: boolean;
          scope: 'h3g' | 'all';
        };
        slots_used: number;
        pipeline: Array<{
          iccid: string;
          email: string;
          stage: string;
          sent_at: string | null;
          days_since: number;
          snapshot_gb: number;
          current_gb: number | null;
          plan_gb: number;
          over_cost_gbp: number;
          hard_limit_gb: number;
          final_limit_gb: number;
          remaining_gb: number | null;
          over_final: boolean;
        }>;
        watching: Array<{
          iccid: string;
          email: string;
          plan_gb: number;
          used_gb: number;
          projected_gb: number;
          over_cost_gbp: number;
          operator_name: string;
        }>;
      },
      { }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'admin/overuse-status',
        body
      })
    }),
    // Team-only: persist dunning settings (scope: h3g default / all).
    setOveruseScope: build.mutation<
      { success: boolean; scope: 'h3g' | 'all' },
      {  scope: 'h3g' | 'all' }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'admin/overuse-settings',
        body
      })
    }),
    // Team-only: trigger a dunning run. dry_run defaults to true server-side.
    runOveruseDunning: build.mutation<
      { success: boolean; dry_run: boolean; counts: Record<string, number> },
      {  mode: 'warn' | 'enforce' | 'reset' }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'admin/overuse-dunning',
        body
      })
    }),
    // Team-only: render the overuse dunning email templates for preview.
    overuseEmailPreview: build.mutation<
      {
        success: boolean;
        min_cost_gbp: number;
        max_recipients: number;
        scope: 'h3g' | 'all';
        hard_limit_gb: number;
        grace_gb: number;
        candidate_count: number;
        total_overusers: number;
        captured_cost_gbp: number;
        total_overuse_cost_gbp: number;
        candidates: Array<{
          iccid: string;
          email: string;
          plan_gb: number;
          used_gb: number;
          projected_gb: number;
          over_cost_gbp: number;
          operator_name: string;
        }>;
        sample_iccid: string;
        sample_email: string;
        templates: { stage1: string; stage2: string; barred: string; reset: string };
      },
      { }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'admin/overuse-email-preview',
        body
      })
    }),
    // Team-only: send yourself [TEST] copies of all four customer emails.
    overuseTestEmails: build.mutation<
      { success: boolean; recipient: string; sent: string[] },
      { }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'admin/overuse-test-emails',
        body
      })
    }),
    // Team-only: bar/unbar/terminate a single orphan SIM in Pelion.
    orphanSimAction: build.mutation<
      { success: boolean; iccid: string; action: string; message: string },
      {  iccid: string; action: 'bar' | 'unbar' | 'terminate' }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'admin/orphan-sim-action',
        body
      })
    }),
    // Google Workspace creates a named, expiring and revocable staff session.
    teamGoogleLogin: build.mutation<
      {
        success: boolean;
        message: string;
        token: string;
        email: string;
      },
      { credential: string }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'auth/team-google-login',
        body
      })
    })
  })
});

export const {
  useRequestLoginMutation,
  useLazyLoginQuery,
  useGetUserDataQuery,
  useGenerateShareLinkMutation,
  useTeamSearchUsersMutation,
  useTeamSwitchUserMutation,
  useTeamGoogleLoginMutation,
  useSimHealthReportMutation,
  useOrphanSimActionMutation,
  useOveruseEmailPreviewMutation,
  useOveruseTestEmailsMutation,
  useOveruseDunningStatusMutation,
  useRunOveruseDunningMutation,
  useSetOveruseScopeMutation
} = authApi;
